import { useEffect, useRef } from 'react';


import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { Checkbox } from 'ui/checkbox';

import SpecialAwardCriteriaBlock from './special-award-criteria-block';

import Icons from 'assets/icons';

import { SPECIAL_AWARD_PRESETS } from '../calculations-step.constants';
import type { UseSpecialAwardFormResult } from '../hooks/use-special-award-form';
import type { UseSpecialAwardModalDataResult } from '../hooks/use-special-award-modal-data';
import type { SpecialAwardCriteria } from '../calculations-step.types';

export interface SpecialAwardPopulationFieldsProps {
  form: UseSpecialAwardFormResult;
  data: UseSpecialAwardModalDataResult;
  criteriaOptions?: ReadonlyArray<{
    value: SpecialAwardCriteria;
    label: string;
  }>;
}

/**
 * The population half of the "Add special award" modal: the
 * predefined-population checkboxes are always visible, and zero or more
 * independent "Criteria" blocks can be added alongside them — both
 * contribute to the same award simultaneously.
 */
const SpecialAwardPopulationFields = ({
  form,
  data,
  criteriaOptions,
}: SpecialAwardPopulationFieldsProps) => {
  const criteriaBlocksCount = form.criteriaBlocks.length;
  const lastBlock = form.criteriaBlocks[criteriaBlocksCount - 1];
  const lastBlockType = lastBlock?.type;
  const lastBlockDimension = lastBlock?.criteria;
  const criteriaListRef = useRef<HTMLDivElement>(null);
  const previousCountRef = useRef(criteriaBlocksCount);
  const previousLastBlockTypeRef = useRef(lastBlockType);
  const previousLastBlockDimensionRef = useRef(lastBlockDimension);

  useEffect(() => {
    const countIsUnchanged = criteriaBlocksCount === previousCountRef.current;
    const blockWasAdded = criteriaBlocksCount === previousCountRef.current + 1;

    const lastBlockFieldWasJustPicked = (
      previous: unknown,
      current: unknown
    ): boolean => countIsUnchanged && !previous && !!current;

    if (
      blockWasAdded ||
      lastBlockFieldWasJustPicked(
        previousLastBlockTypeRef.current,
        lastBlockType
      ) ||
      lastBlockFieldWasJustPicked(
        previousLastBlockDimensionRef.current,
        lastBlockDimension
      )
    ) {
      const list = criteriaListRef.current;

      list?.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
    }

    previousCountRef.current = criteriaBlocksCount;
    previousLastBlockTypeRef.current = lastBlockType;
    previousLastBlockDimensionRef.current = lastBlockDimension;
  }, [criteriaBlocksCount, lastBlockType, lastBlockDimension]);

  return (
    <>
      <fieldset className="m-0 border-0 p-0">
        <legend className="sr-only">Predefined populations</legend>
        <Stack direction="row" sx={{ gap: 2 }}>
          {SPECIAL_AWARD_PRESETS.map((preset) => (
            <Checkbox
              key={preset.key}
              id={`special-award-preset-${preset.key}`}
              label={preset.label}
              checked={form.appliesTo[preset.key]}
              onChange={() =>
                form.setApplies(preset.key, !form.appliesTo[preset.key])
              }
              data-testid={`checkbox-special-award-preset-${preset.key}`}
            />
          ))}
        </Stack>
      </fieldset>

      {!!criteriaBlocksCount && (
        <div
          ref={criteriaListRef}
          className="flex max-h-[320px] flex-col gap-5 overflow-y-auto scrollbar-thin"
        >
          {form.criteriaBlocks.map((block) => (
            <SpecialAwardCriteriaBlock
              key={block.blockId}
              block={block}
              form={form}
              data={data}
              criteriaOptions={criteriaOptions}
            />
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="reserve-outlined"
        size="sm"
        className="w-fit"
        iconBefore={<Icons.Plus />}
        onClick={form.addCriteriaBlock}
        data-testid="add-criteria-block-button"
      >
        Add criteria
      </Button>
    </>
  );
};

export default SpecialAwardPopulationFields;
